import { group } from "d3";
import dayjs from "dayjs";
import { groupBy } from "lodash";

/**
 * @description Global reusable functions
 */
export default {
  /**
   * @description create dynamic grid cols
   * @returns
   * @param cols
   */
  dynamicCols(cols) {
    return { gridTemplateColumns: `repeat(${cols}, 1fr)` };
  },

  /**
   * @description create dynamic grid cols v2
   * @returns
   * @param cols
   */
  dynamicColsV2(...cols) {
    return { gridTemplateColumns: `${cols.map(col => `${col}fr`).join(' ')}` };
  },

  /**
   * @description Convert PascalCase string into Pascal Case with spaces
   * @param {string} str
   * @returns {string}
   */
  formatTitle(str) {
    return str.replace(/([A-Z])/g, " $1").trim();
  },

  /**
   * @description Convert camelCase string into Camel Case with spaces
   * @param {string} text
   * @returns {string}
   */
  formatCamelCase(text) {
    return text
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/[_-]+/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  },

  /**
   * @description Create a dynamic multiple validation message
   * @param {*} validations
   * @returns {string}
   */
  createMultipleValidations(validations) {
    return validations.map((y, index) => {
      const key = this.formatCamelCase(y.includes("Id") ? y.split("Id")[0] : y);

      return index + 1 < validations.length
        ? `${key}, `
        : `${key} ${validations.length === 1 ? "is" : "are"} required. `;
    });
  },

  /**
   * Extracts the time part from a given Date object.
   *
   * @returns {string} The time part of the date in the format 'HH:MM:SS'.
   * @param dateStr
   */
  getTimePartOfDate(dateStr) {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "UTC",
    });
  },

  /**
   *
   * @description Deep copy an object
   * @param {*} obj
   * @returns
   */
  deepCopy(obj) {
    return JSON.parse(JSON.stringify(obj));
  },

  /**
   *
   * @description Send toast response to client
   * @param {*} res
   * @param {*} error
   * @param {*} data
   * @param {*} status
   * @returns response
   */
  sendResponse(error) {
    if (!error) return;

    let errMessage = "";

    switch (true) {
      case error instanceof Error:
        errMessage = error.message;
        break;
      case typeof error === "string":
        errMessage = error;
        break;
      case typeof error === "object":
        errMessage = JSON.stringify(error, null, 2);
        break;
      default:
        errMessage = `Unknown error: ${error}`;
    }

    return errMessage;
  },

  /**
   * @description Formats a date into MM/DD/YYYY
   * @param {*} dateStr
   * @returns {string} Formatted date
   */
  formatDate(dateStr) {
    const date = new Date(dateStr);
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    const year = date.getUTCFullYear();

    return `${month}/${day}/${year}`;
  },

  /**
   * @description Formats time
   * @param {*} dateStr
   * @returns {string} Formatted time
   */
  formatTime(datetime) {
    return dayjs(datetime).format("HH:mm");
  },

  /**
   * @description Get full name format
   * @param {*} obj
   * @returns full name
   */
  getFullName(obj, isReverse = false) {
    if (!obj || typeof obj !== "object" || !obj.firstName || !obj.lastName)
      return "";
    return isReverse
      ? `${obj.lastName}, ${obj.firstName}`
      : `${obj.firstName} ${obj.lastName}`;
  },

  /**
   * @description Format float/integer to a currency
   * @param {*} amount
   * @returns full name
   */
  formatCurrency(amount) {
    if (isNaN(amount)) return "";
    return parseFloat(amount).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  },

  /**
   * @description get status by days margin
   * @param {*} daysMargin
   * @returns status
   */
  getStudentStatus(daysMargin) {
    let status;
    switch (daysMargin) {
      case 0:
        status = "On Time";
        break;
      case daysMargin > 0:
        status = `${daysMargin} day${daysMargin > 1 ? "s" : ""} ahead`;
      case daysMargin < 0:
        status = `${Math.abs(daysMargin)} day${Math.abs(daysMargin) > 1 ? "s" : ""} behind`;
        break;
    }
    return status;
  },

  /**
   * @description remove duplicate objects in array
   * @param {*} array
   * @returns unique props
   */
  removeDuplicate(array) {
    return array.filter((item, index, self) => {
      return (
        index ===
        self.findIndex((t) => {
          return JSON.stringify(t) === JSON.stringify(item);
        })
      );
    });
  },

  /**
   * Groups an array of objects by specified keys and optionally adds a count of items in each group.
   *
   * @param {Array<Object>} array - The array of objects to be grouped.
   * @param {Array<string>} [keys=[]] - The keys to group by. Each key should be a property name of the objects in the array.
   * @param {boolean} [getAccumulationCount=false] - Whether to add a count of items in each group.
   * @returns {Array<Object>} An array of grouped objects. Each object represents a group and contains the properties of the first item in the group. If getAccumulationCount is true, each object also contains a count property indicating the number of items in the group.
   */
  groupArrayByKeys(array, keys = [], getAccumulationCount = false) {
    if (!Array.isArray(array) || array.length === 0) {
      return {};
    }
    const resultMap = array.reduce((result, currentValue) => {
      const groupKey = keys.map((key) => currentValue[key]).join("-");
      if (!result[groupKey]) {
        result[groupKey] = [];
      }

      result[groupKey].push(currentValue);

      return result;
    }, {});

    const resultArray = Object.keys(resultMap).map((key) => {
      const group = resultMap[key];
      const resultItem = { ...group[0] };

      if (getAccumulationCount) {
        resultItem.count = group.length;
      }

      return resultItem;
    });

    return resultArray;
  },


  /**
   * @description Get the closest color based on the score
   * @param {*} arr 
   * @param {*} score 
   * @returns 
   */
  scoreColor(arr, score) {
    let closest = arr[0].color;
    for (let i = 1; i < arr.length; i++) {
      if (score <= arr[i].stop) {
        closest = arr[i].color;
        break;
      }
    }
    return closest;
  }
};
